// feed-capture.ts — Phase 0 of the WebSocket odds-capture plan
// (docs/digitain-ws-capture-plan.md).
//
// The Digitain LiveEventWidget boots in the PARENT page and pulls its live odds
// over a WebSocket. Reading that socket is the real odds source — the React
// `StakeType` we were scraping is just a lagging projection of it. This module
// installs a passive WebSocket interceptor so a live capture session can learn
// the feed's endpoint, subprotocol, and message shape.
//
// Design notes (from the Mirador ganamos/1win/betboom capture playbook):
//  - Install SYNCHRONOUSLY at module load, before `bootLandingWidgets` runs, so
//    the widget's socket is wrapped from construction (feeds connect in ~1-2s).
//  - Capture is PASSIVE: we add our own `message` listener and wrap `send`; we
//    never replace the app's handlers, so the sportsbook keeps working. (We do
//    not need to intercept the `onmessage` setter for read-only capture — our
//    addEventListener fires independently of however the app listens.)
//  - The feed sometimes lives in a Web/SharedWorker, where a `window.WebSocket`
//    hook sees nothing. We can't safely rewrite a cross-origin worker on a live
//    operator site, so Phase 0 only RECORDS worker creation (url + type); if the
//    capture shows the odds are in a worker, we escalate deliberately in a later
//    phase (own-client subscription or careful worker injection).

const HOOK_FLAG = "__bcFeedHooked";
const GLOBAL_KEY = "__bcFeed";

export interface FeedFrame {
  t: number; // ms since capture install
  url: string;
  dir: "in" | "out";
  kind: "text" | "binary";
  size: number;
  transport: string; // json | messagepack | protobuf | graphql-ws | engineio | text | binary
  text?: string; // text payload (capped unless full)
  head?: number[]; // first bytes (binary, for classification)
  b64?: string; // binary payload, capped (only when full)
}

export interface FeedState {
  enabled: boolean; // buffer frames
  full: boolean; // retain full/large payloads
  max: number; // ring-buffer cap
  t0: number;
  frames: FeedFrame[];
  sockets: Array<{ url: string; proto: string; opened: number }>;
  workers: Array<{ url: string; type: "worker" | "shared" }>;
  onFrame?: (f: FeedFrame) => void;
}

const B64_CAP = 8000;
const TEXT_CAP = 4000;

function nowMs(): number {
  try {
    return Math.round(performance.now());
  } catch {
    return Date.now();
  }
}

// Classify a frame's wire format from its payload / subprotocol.
export function classifyFrame(
  text: string | null,
  head: number[],
  proto: string,
): string {
  if (proto && proto.toLowerCase().indexOf("graphql") >= 0) return "graphql-ws";
  if (text != null) {
    const s = text.replace(/^\s+/, "");
    if (s[0] === "{" || s[0] === "[") return "json";
    // Engine.IO / Socket.IO text frames begin with a digit packet type.
    if (/^[0-9]/.test(s)) return "engineio";
    return "text";
  }
  const b = head[0];
  if (b === 0xc4 || b === 0xc5 || b === 0xc6) return "messagepack"; // bin8/16/32
  if (b === 0xdc || b === 0xdd || (b >= 0x90 && b <= 0x9f)) return "messagepack"; // arrays
  if (b === 0xde || b === 0xdf || (b >= 0x80 && b <= 0x8f)) return "messagepack"; // maps
  if (b === 0x0a || b === 0x12 || b === 0x08 || b === 0x1a || b === 0x22)
    return "protobuf"; // common field tags
  return "binary";
}

async function toParts(
  data: unknown,
): Promise<{ text: string | null; bytes: Uint8Array | null }> {
  if (typeof data === "string") return { text: data, bytes: null };
  if (data instanceof ArrayBuffer)
    return { text: null, bytes: new Uint8Array(data) };
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    const buf = await data.arrayBuffer();
    return { text: null, bytes: new Uint8Array(buf) };
  }
  if (data && ArrayBuffer.isView(data as ArrayBufferView)) {
    const v = data as ArrayBufferView;
    return {
      text: null,
      bytes: new Uint8Array(v.buffer, v.byteOffset, v.byteLength),
    };
  }
  return { text: null, bytes: null };
}

function b64(bytes: Uint8Array): string {
  let s = "";
  const n = Math.min(bytes.length, B64_CAP);
  for (let i = 0; i < n; i++) s += String.fromCharCode(bytes[i]);
  try {
    return btoa(s);
  } catch {
    return "";
  }
}

function makeRecorder(state: FeedState) {
  return async function record(
    url: string,
    dir: "in" | "out",
    data: unknown,
    proto: string,
  ): Promise<void> {
    if (!state.enabled) return;
    try {
      const { text, bytes } = await toParts(data);
      let frame: FeedFrame;
      if (text != null) {
        frame = {
          t: nowMs() - state.t0,
          url,
          dir,
          kind: "text",
          size: text.length,
          transport: classifyFrame(text, [], proto),
          text: state.full ? text : text.slice(0, TEXT_CAP),
        };
      } else if (bytes != null) {
        const head = Array.from(bytes.slice(0, 8));
        frame = {
          t: nowMs() - state.t0,
          url,
          dir,
          kind: "binary",
          size: bytes.length,
          transport: classifyFrame(null, head, proto),
          head,
        };
        if (state.full) frame.b64 = b64(bytes);
      } else {
        return;
      }
      state.frames.push(frame);
      if (state.frames.length > state.max) state.frames.shift();
      if (state.onFrame) {
        try {
          state.onFrame(frame);
        } catch {
          /* listener error is not our problem */
        }
      }
    } catch {
      /* never let capture throw into the app */
    }
  };
}

function hookWebSocket(w: Record<string, unknown>, state: FeedState): void {
  const NativeWS = w.WebSocket as unknown as {
    new (url: string, protocols?: string | string[]): WebSocket;
    prototype: WebSocket;
    CONNECTING: number;
    OPEN: number;
    CLOSING: number;
    CLOSED: number;
  };
  if (typeof NativeWS !== "function") return;
  const record = makeRecorder(state);

  const Wrapped = function (
    this: unknown,
    url: string,
    protocols?: string | string[],
  ): WebSocket {
    const ws =
      protocols !== undefined ? new NativeWS(url, protocols) : new NativeWS(url);
    try {
      state.sockets.push({
        url: String(url),
        proto: String(
          ws.protocol ||
            (Array.isArray(protocols) ? protocols.join(",") : protocols || ""),
        ),
        opened: nowMs() - state.t0,
      });
      ws.addEventListener("message", (e: MessageEvent) => {
        void record(String(url), "in", e.data, ws.protocol);
      });
      const origSend = ws.send.bind(ws);
      ws.send = function (d: unknown): void {
        try {
          void record(String(url), "out", d, ws.protocol);
        } catch {
          /* ignore */
        }
        return origSend(d as never);
      };
    } catch {
      /* if wrapping fails, the socket still works untouched */
    }
    return ws;
  } as unknown as typeof NativeWS;

  // Preserve prototype + static constants so `instanceof` and readyState refs work.
  Wrapped.prototype = NativeWS.prototype;
  Wrapped.CONNECTING = NativeWS.CONNECTING;
  Wrapped.OPEN = NativeWS.OPEN;
  Wrapped.CLOSING = NativeWS.CLOSING;
  Wrapped.CLOSED = NativeWS.CLOSED;
  try {
    Object.setPrototypeOf(Wrapped, NativeWS);
  } catch {
    /* non-fatal */
  }
  w.WebSocket = Wrapped;
}

// Record worker creation so a capture session reveals whether the feed lives in
// a worker (where the window.WebSocket hook can't reach). Detection only — we do
// NOT rewrite the worker on a live operator site.
function hookWorkers(w: Record<string, unknown>, state: FeedState): void {
  for (const [key, kind] of [
    ["Worker", "worker"],
    ["SharedWorker", "shared"],
  ] as const) {
    const Native = w[key] as unknown as (new (
      url: string | URL,
      options?: unknown,
    ) => unknown) & { prototype: unknown };
    if (typeof Native !== "function") continue;
    const Wrapped = function (
      this: unknown,
      url: string | URL,
      options?: unknown,
    ): unknown {
      try {
        state.workers.push({ url: String(url), type: kind });
      } catch {
        /* ignore */
      }
      return options !== undefined
        ? new Native(url, options)
        : new Native(url);
    } as unknown as typeof Native;
    Wrapped.prototype = Native.prototype;
    try {
      Object.setPrototypeOf(Wrapped, Native);
    } catch {
      /* non-fatal */
    }
    w[key] = Wrapped;
  }
}

// Install the interceptor. Idempotent. `capture` gates frame buffering; socket &
// worker creation are always recorded (cheap) so we can see the transport even
// without full capture. Returns the shared FeedState (also on window.__bcFeed).
export function installFeedCapture(opts: {
  capture: boolean;
  full?: boolean;
  max?: number;
}): FeedState | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as Record<string, unknown>;
  if (w[HOOK_FLAG]) {
    const existing = w[GLOBAL_KEY] as FeedState;
    existing.enabled = existing.enabled || opts.capture;
    if (opts.full) existing.full = true;
    return existing;
  }
  w[HOOK_FLAG] = true;

  const state: FeedState = {
    enabled: !!opts.capture,
    full: !!opts.full,
    max: opts.max ?? 3000,
    t0: nowMs(),
    frames: [],
    sockets: [],
    workers: [],
  };
  w[GLOBAL_KEY] = state;

  try {
    hookWebSocket(w, state);
    hookWorkers(w, state);
  } catch {
    /* if hooking blows up, the page is unaffected */
  }

  // Compact summary for a live capture session: transports seen, endpoints,
  // whether any worker was created, and a few sample frames.
  (w as Record<string, unknown>).__bcFeedDump = function (): unknown {
    const transports: Record<string, number> = {};
    for (const f of state.frames)
      transports[f.transport] = (transports[f.transport] || 0) + 1;
    return {
      enabled: state.enabled,
      frameCount: state.frames.length,
      transports,
      sockets: state.sockets,
      workers: state.workers,
      sample: state.frames.slice(0, 12),
    };
  };

  return state;
}
